import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const inquirySchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(50).optional().default(""),
  service: z.string().trim().max(120).optional().default(""),
  zip: z.string().trim().max(20).optional().default(""),
  message: z.string().trim().max(5000).optional().default(""),
  source: z.enum(["contact_form", "booking_dialog"]).default("contact_form"),
});

export type InquiryInput = z.input<typeof inquirySchema>;

const OWNER_EMAIL = "rkdtile@msn.com";

function esc(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export const submitInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => inquirySchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { error: insertError } = await supabaseAdmin
      .from("contact_inquiries")
      .insert({
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        service: data.service || null,
        zip: data.zip || null,
        message: data.message || null,
        source: data.source,
      });

    if (insertError) {
      console.error("Failed to store inquiry", insertError);
      throw new Error("We couldn't send your request. Please call (801) 598-2226.");
    }

    const rows: Array<[string, string]> = [
      ["Name", data.name],
      ["Email", data.email],
      ["Phone", data.phone || "—"],
      ["Service", data.service || "—"],
      ["Zip code", data.zip || "—"],
      ["Message", data.message || "—"],
      ["Source", data.source === "booking_dialog" ? "Booking dialog" : "Contact form"],
    ];

    const html = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;color:#1a1a1a">
      <h2 style="font-size:18px;margin:0 0 16px">New consultation request — rkdtile.com</h2>
      <table cellpadding="6" style="border-collapse:collapse">
        ${rows
          .map(
            ([k, v]) =>
              `<tr><td style="font-weight:bold;vertical-align:top">${esc(k)}</td><td>${esc(v).replace(/\n/g, "<br>")}</td></tr>`,
          )
          .join("")}
      </table>
      <p style="margin-top:20px;color:#666;font-size:13px">Reply directly to ${esc(data.email)} to reach this customer.</p>
    </div>`;

    const text = rows.map(([k, v]) => `${k}: ${v}`).join("\n");

    const messageId = crypto.randomUUID();

    const { error: enqueueError } = await supabaseAdmin.rpc("enqueue_email", {
      queue_name: "transactional_emails",
      payload: {
        message_id: messageId,
        to: OWNER_EMAIL,
        from: "RKD Tile <noreply@rkdtile.com>",
        sender_domain: "notify.rkdtile.com",
        reply_to: data.email,
        subject: `New consultation request — ${data.name}`,
        html,
        text,
        purpose: "transactional",
        label: "contact_inquiry",
        idempotency_key: messageId,
        queued_at: new Date().toISOString(),
      },
    });

    if (enqueueError) {
      // The inquiry is safely stored; surface success but log the delivery issue.
      console.error("Failed to enqueue inquiry notification", enqueueError);
    }

    return { ok: true as const };
  });
